/**
 * The features a car offers, or an order asks for, as a row of toggles.
 *
 * Any number may be on, so each chip is pressed or not on its own — never a group where
 * pressing one releases another, which is what made "an estate that takes dogs" impossible
 * when these were classes (#369).
 *
 * Only labels in use are offered; a switched-off one is drawn only while it is chosen, marked
 * as switched off, so it can be unticked (`offeredFeatures`). A label with a picture shows it
 * beside its words, small, as the passenger app shows it larger (#462).
 */

import { useTranslation } from 'react-i18next';

import { Chip, ChipGroup } from '../ui';
import type { VehicleFeature } from './api';
import { featureName, featureShortName, offeredFeatures } from './words';

export function FeatureChips({
  features,
  chosen,
  onChange,
  label,
  short = false,
  enterSubmits = false,
}: {
  /** Every label, switched-off ones included; which to draw is decided here. */
  features: readonly VehicleFeature[];
  chosen: readonly string[];
  /**
   * Told the change rather than the new list, so two presses in one moment both count: a
   * list computed from what this render saw would lose the first.
   */
  onChange: (change: (chosen: string[]) => string[]) => void;
  label: string;
  /** One word each, for the order form's narrow column; the full name is the chip's title. */
  short?: boolean;
  /**
   * Enter takes the order instead of pressing the chip, which is left to Space.
   *
   * The order form is driven from the keyboard, and its header promises Enter as "take the
   * order". A chip is a button, and a button presses itself on Enter, so a
   * dispatcher who Tabbed to a requirement and pressed Enter to take the call toggled the
   * requirement instead — and then had to notice. The vehicle editor has no such promise
   * and keeps the button's own behaviour.
   */
  enterSubmits?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <ChipGroup label={label}>
      {offeredFeatures(features, chosen).map((feature) => {
        const on = chosen.includes(feature.code);
        const name = featureName(t, feature.code, feature.displayName, feature.namedByFirm);

        return (
          <Chip
            key={feature.code}
            current={on}
            className="gap-1.5"
            title={
              feature.isActive
                ? short
                  ? name
                  : undefined
                : t('labels.switchedOffHelp', { name })
            }
            onKeyDown={
              enterSubmits
                ? (event) => {
                    if (event.key !== 'Enter') return;
                    // Before the browser turns the key into a click on the chip.
                    event.preventDefault();
                    event.currentTarget.form?.requestSubmit();
                  }
                : undefined
            }
            onClick={() =>
              onChange((current) =>
                current.includes(feature.code)
                  ? current.filter((code) => code !== feature.code)
                  : [...current, feature.code],
              )
            }
          >
            {feature.pictureUrl !== null && (
              // Same origin, as the logo and the labels page are; the URL carries its version.
              <img src={feature.pictureUrl} alt="" className="h-4 w-4 object-contain shrink-0" />
            )}
            {short
              ? featureShortName(t, feature.code, feature.displayName, feature.namedByFirm)
              : name}
            {!feature.isActive && <span className="font-normal">· {t('labels.switchedOff')}</span>}
          </Chip>
        );
      })}
    </ChipGroup>
  );
}
